/**
 * Structural deep equality for JSON-like values (primitives, plain
 * objects, arrays). Replaces `JSON.stringify` comparisons, whose
 * correctness depends on engine-defined property iteration order —
 * fragile per the ECMAScript spec.
 *
 * Constrained to the editor state shape: strings, numbers, booleans,
 * null, plain objects, and arrays. Date, Map, Set, RegExp, and class
 * instances aren't handled — the editor state never contains them.
 */
export function isDeepEqual(a: unknown, b: unknown): boolean {
  // `Object.is` handles primitives correctly (NaN === NaN; +0 !== -0)
  // and short-circuits identical references.
  if (Object.is(a, b)) return true;

  if (a === null || b === null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;

  if (aIsArray) {
    const arrA = a as unknown[];
    const arrB = b as unknown[];
    if (arrA.length !== arrB.length) return false;
    for (let i = 0; i < arrA.length; i++) {
      if (!isDeepEqual(arrA[i], arrB[i])) return false;
    }
    return true;
  }

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  if (keysA.length !== Object.keys(objB).length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
    if (!isDeepEqual(objA[key], objB[key])) return false;
  }
  return true;
}
